import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Spinner, Alert } from "react-bootstrap";

import { useTitle } from "./context";
import { SpellBookBackend } from "./backend";
import { storeState, loadState } from "./utilities";
import { ClassLevelOrderedSpellsList } from "./spellsList";

import "bootstrap/dist/css/bootstrap.min.css";


const CLASS_PAGE_KEY = "class-page";


async function fetchSpells(classId) {
    return await SpellBookBackend.listSpellsByClass(classId);
}


export function ClassPage({navbarHeight}) {
    const { classId } = useParams();

    const navigate = useNavigate();
    const { setTitle } = useTitle();
    const [classInfo, setClassInfo] = useState(null);
    const [spells, setSpells] = useState([]);
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [error, setError] = useState("");

    const getStateJson = () => {
        return {
            classId: classId,
            classInfo: classInfo,
            spells: spells
        };
    };

    const setStateJson = (s) => {
        if (s.classId !== classId) throw new Error("Attempting to set a state with mismatching class ID");
        setClassInfo(s.classInfo);
        setSpells(s.spells);
    };
    
    useEffect(() => {
        setTitle(classInfo?.name);
    }, [setTitle, classInfo]);

    useEffect(() => {
        if (classInfo && spells && spells.length > 0) {
            storeState(CLASS_PAGE_KEY, getStateJson());
        }
    }, [classInfo, spells]);

    const fetchData = async () => {
        setClassInfo(await SpellBookBackend.getClass(classId));
        setSpells(await fetchSpells(classId));
    }

    useEffect(() => {
        let mounted = true;
        (
            async () => {
                try {
                    setStatus("loading");
                    const s = loadState(CLASS_PAGE_KEY);
                    if (s?.classId === classId && s?.spells?.length > 0) {
                        setStateJson(s);
                    }
                    else {
                        await fetchData();
                    }
                    if (mounted) {
                        setStatus("success");
                    }
                }
                catch {
                    if (mounted) {
                        setError("Failed to load items.");
                        setStatus("error");
                    }
                }
            }
        )();
        return () => {
            mounted = false;
        };
    }, []);

    const content = useMemo(() => {
        if (status === "loading") {
            return (
                <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" role="status" aria-label="Loading items" />
                </div>
            );
        }
        if (status === "error") {
            return <Alert variant="danger" className="my-3">{error}</Alert>;
        }
        if (spells.length === 0) {
            return <Alert variant="secondary" className="my-3">No spells found.</Alert>;
        }

        //else

        const spellsListHeight = `calc(100vh - ${navbarHeight}px)`;

        return (
            <ClassLevelOrderedSpellsList
            stateKey={`${CLASS_PAGE_KEY}.${classId}`}
            spells={spells}
            classId={parseInt(classId)}
            onSpellClick={(spellId) => navigate(`/spells/${spellId}`)}
            height={spellsListHeight}/>
        );
    }, [status, spells, error, navigate]);

    return (
        <Container
        fluid
        className="bg-light p-0"
        style={{ marginTop: `${navbarHeight}px` }}>
            {content}
        </Container>
    );
}
